import { Container, FormGroup } from "react-bootstrap";
import React, { useEffect, useRef, useState } from "react";
import {
    allocationsGridModes,
    formModes,
} from "shared/constants/allocationsEnums";
import {
    useStylesForButton,
    useStylesForButtonGroups,
    useStylesForText,
} from "shared/styles/makeStyles";

import Button from "shared/components/inputs/mui/Button";
import Link from "shared/components/navigation/links/AnchorLink";
import List from "shared/constants/allocationLists";
import Loader from "shared/components/feedback/loading/Loader";
import Modal from "react-bootstrap/Modal";
import PropTypes from "prop-types";
import Text from "shared/components/data-display/mui/Text";
import TextAreaInputWithValidation from "shared/components/inputs/text-input/TextAreaInputWithValidation";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import { makeStyles } from "@material-ui/core";
import moment from "moment";
import { sendNotification } from "shared/components/feedback/notification/notification";
import useAllocationsNoteRead from "features/allocations/hooks/useAllocationsNoteRead";
import useAllocationsNoteUpdate from "features/allocations/hooks/useAllocationsNoteUpdate";
import { useForm } from "react-hook-form";
import {
    Content,
    FormRow,
    FormColumn,
    FormField,
    Action,
    SecondaryButtonWrapper,
    StyledSpecifyDaysText,
} from "./Note.styles";

const useStyles = makeStyles(() => ({
    root: {
        "& .MuiToggleButton-root": {
            padding: "0.125rem 0.375rem",
        },
        "& .MuiToggleButtonGroup-root": {
            width: "100%",
        },
        "& .MuiToggleButtonGroup-groupedHorizontal:not(:first-child)": {
            borderLeft: globalThis.config.border.small,
        },
    },
    title: {
        "& .modal-title": {
            width: "100%",
            margin: "1rem 1rem 1rem 1rem",
        },
    },
    header: {
        height: "5rem",
    },
}));

const Note = ({
    mode,
    title,
    employee,
    project,
    date,
    onAddRemoveNote,
    requestHide,
    isNewNote,
    readOnly,
}) => {
    const { saveNote, deleteNote } = useAllocationsNoteUpdate({
        employeeId: employee.userId,
        jobId: project.jobId,
        titleId: employee.titleId,
        officeId: employee.officeId,
        date,
        mode: allocationsGridModes.weekly,
        isJobScope: project.isJobScope,
        isNewNote: isNewNote,
    });

    const { note } = useAllocationsNoteRead({
        employeeId: employee.userId,
        jobId: project.jobId,
        titleId: employee.titleId,
        officeId: employee.officeId,
        date,
        mode: allocationsGridModes.weekly,
        isJobScope: project.isJobScope,
        isNewNote: isNewNote,
    });

    const {
        control,
        formState: { errors },
        setValue,
        setFocus,
        handleSubmit,
    } = useForm({
        mode: "onChange",
        defaultValues: { note: note.data?.note ?? "" },
    });

    const buttonGroupClasses = useStylesForButtonGroups();
    const buttonClasses = useStylesForButton();
    const classesText = useStylesForText();
    const classes = useStyles();
    const inputRef = useRef();
    const [days, setDays] = useState(note.data?.weekdays ?? []);
    const mutationInProgress = saveNote.isLoading || deleteNote.isLoading;
    const readOnlyInPast = moment(date) < moment().startOf("isoWeek");
    const formDisabled = note.isLoading || readOnlyInPast;

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(
                inputRef.current.value.length,
                inputRef.current.value.length
            );
        }
    }, []);

    useEffect(() => {
        if (note.data) {
            setValue("note", note.data.note);
            setDays(note.data.weekdays);
            inputRef.current.setSelectionRange(
                inputRef.current.value.length,
                inputRef.current.value.length
            );
        }
    }, [setValue, note.data, setFocus]);

    const formatDate = (date) => {
        return (
            moment(date).format("MMM D") +
            " - " +
            moment(date).add(6, "days").format("MMM D")
        );
    };

    const snackBarMessage = () => {
        return (
            <>
                A note on <b>{employee.fullName}</b> has been{" "}
                {mode === formModes.ADD ? "saved" : "edited"}.
            </>
        );
    };

    const handleDay = (event, newDays) => {
        setDays(newDays);
    };

    const handleSave = async function (formValues) {
        if (readOnlyInPast) {
            return false;
        }

        await saveNote.mutateAsync({ note: formValues.note, days });
        onAddRemoveNote(true, formValues.note);
        sendNotification(snackBarMessage());
        requestHide();
    };

    const handleDelete = async function () {
        if (readOnlyInPast) {
            return false;
        }

        await deleteNote.mutateAsync();
        onAddRemoveNote(false, "");
        sendNotification(
            <>
                A note on <b>{employee.fullName}</b> has been deleted.
            </>
        );
        requestHide();
    };

    return (
        <Modal.Body style={{ overflow: "hidden" }}>
            <Container fluid>
                <Content
                    className={classes.root}
                    onSubmit={handleSubmit(handleSave)}
                >
                    <FormRow>
                        <FormColumn>
                            <Text
                                variant="xsmall"
                                className={classesText.gray2}
                            >
                                {employee.fullName}
                            </Text>
                            <Text variant="smedium">{project.jobName}</Text>
                        </FormColumn>
                        <FormColumn>
                            <Text
                                variant="xsmall"
                                className={classesText.gray2}
                            >
                                Week of
                            </Text>
                            <Text variant="smedium">{formatDate(date)}</Text>
                        </FormColumn>
                    </FormRow>
                    <FormGroup>
                        <TextAreaInputWithValidation
                            required={true}
                            control={control}
                            errors={errors}
                            label={readOnly ? "Note" : "Add a note"}
                            name="note"
                            disabled={formDisabled || readOnly}
                            ref={inputRef}
                        />
                    </FormGroup>
                    <FormField>
                        <StyledSpecifyDaysText>
                            Specify Days (Optional)
                        </StyledSpecifyDaysText>
                        <ToggleButtonGroup
                            value={days}
                            onChange={handleDay}
                            disabled={readOnly}
                            aria-label="text displays"
                        >
                            {List.days().map((day) => (
                                <ToggleButton
                                    key={day.value}
                                    value={day.value}
                                    aria-label="left aligned"
                                    className={buttonGroupClasses.customStyles}
                                    disabled={formDisabled || readOnly}
                                >
                                    <Text variant="xsmall">{day.label}</Text>
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </FormField>
                    {!readOnly && (
                        <Action>
                            {mutationInProgress ? (
                                <Loader
                                    height="50px"
                                    width="50px"
                                    style={{ paddingLeft: "0px" }}
                                />
                            ) : (
                                <>
                                    {mode === formModes.ADD && (
                                        <>
                                            <Link
                                                variant="secondary"
                                                onClick={requestHide}
                                            >
                                                Cancel
                                            </Link>
                                            <Button
                                                disabled={formDisabled}
                                                className={[
                                                    buttonClasses.button,
                                                    buttonClasses.noMargin,
                                                    buttonClasses.primaryButton,
                                                ]}
                                                type="submit"
                                            >
                                                Save
                                            </Button>
                                        </>
                                    )}
                                    {mode === formModes.DETAIL && (
                                        <>
                                            <SecondaryButtonWrapper>
                                                <Button
                                                    className={[
                                                        buttonClasses.button,
                                                        buttonClasses.secondary,
                                                        buttonClasses.noMargin,
                                                        buttonClasses.smallSize,
                                                    ]}
                                                    onClick={handleDelete}
                                                    disabled={formDisabled}
                                                    type="button"
                                                >
                                                    Delete
                                                </Button>
                                            </SecondaryButtonWrapper>
                                            <Button
                                                className={[
                                                    buttonClasses.button,
                                                    buttonClasses.noMargin,
                                                    buttonClasses.primaryButton,
                                                ]}
                                                disabled={formDisabled}
                                                type="submit"
                                            >
                                                Save
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </Action>
                    )}
                </Content>
            </Container>
        </Modal.Body>
    );
};

Note.propTypes = {
    mode: PropTypes.string,
    title: PropTypes.string,
    employee: PropTypes.shape({
        userId: PropTypes.number,
        titleId: PropTypes.number,
        officeId: PropTypes.number,
        name: PropTypes.string,
        fullName: PropTypes.string,
    }),
    project: PropTypes.shape({
        jobId: PropTypes.number,
        isJobScope: PropTypes.bool,
        jobName: PropTypes.string,
    }),
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isNewNote: PropTypes.bool,
    readOnly: PropTypes.bool,
    onAddRemoveNote: PropTypes.func,
    requestHide: PropTypes.func,
};

export default Note;
